import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ReviewDialog({ open, onOpenChange, booking, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [ownerRating, setOwnerRating] = useState(0);
  const [ownerHover, setOwnerHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Please select a rating");
      return;
    }
    if (comment.trim().length < 3) {
      toast.error("Please write at least a few words");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        booking_id: booking.id,
        rating,
        comment,
        owner_rating: ownerRating > 0 ? ownerRating : undefined,
      });
      toast.success("Thanks for your review!");
      onSubmitted?.();
      onOpenChange(false);
      setRating(0);
      setOwnerRating(0);
      setComment("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Review your trip</DialogTitle>
          <div className="text-sm text-muted-foreground">{booking.car_title} · {booking.start_date} → {booking.end_date}</div>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Your rating</div>
            <div className="flex gap-1.5" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= (hover || rating);
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`rating-star-${n}`}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={28}
                      className={filled ? "fill-accent text-accent" : "text-muted-foreground/40"}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Rate the owner&rsquo;s behavior (optional)</div>
            <div className="flex gap-1.5" data-testid="owner-rating-stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= (ownerHover || ownerRating);
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setOwnerHover(n)}
                    onMouseLeave={() => setOwnerHover(0)}
                    onClick={() => setOwnerRating(n)}
                    data-testid={`owner-rating-star-${n}`}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={24}
                      className={filled ? "fill-accent text-accent" : "text-muted-foreground/40"}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Tell others about your experience</div>
            <Textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="The car was clean, owner was great…"
              data-testid="review-comment-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="review-submit-btn">
            {submitting ? "Submitting…" : "Post review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
